/**
 * Zajedničko ponašanje ljuske izveštaja na računaru (/izvestaj i /guest-report): širina i raspored,
 * lepljiva traka, panel sa desne strane (stek), gustina tabele, obaveštenje, pomoć i prečice.
 *
 * Ljuska koja ga koristi daje: ref "root" (koren .rd), opcionalno ref "bar" (traka filtera) i
 * `menuKeys`, `currentKey`, `openScreen(key)`, `reload()`, `shiftDay(step)`.
 */
const DENSITY_KEY = "report-density";
const DEFAULT_TOP = 64;
const DEFAULT_BAR = 56;
const STICKY_BAR_MAX = 64;

const readDensity = () => {
  try {
    return localStorage.getItem(DENSITY_KEY) === "compact" ? "compact" : "comfortable";
  } catch (error) {
    return "comfortable";
  }
};

export default {
  provide() {
    return { reportShell: this };
  },
  data: () => ({
    width: 1440,
    topOffset: DEFAULT_TOP,
    barHeight: DEFAULT_BAR,
    density: readDensity(),
    helpOpen: false,
    panelStack: [],
    panelOpener: null,
    kpis: [],
    loading: false,
    reloadKey: 0,
    toast: null,
  }),
  computed: {
    /** Širina samog izveštaja (bez menija aplikacije); panel je uvek preko sadržaja, pa ne zavisi od nje. */
    layout() {
      if (this.width >= 1300) {
        return "xl";
      }
      if (this.width >= 1140) {
        return "lg";
      }
      return this.width >= 900 ? "md" : "sm";
    },
    /** Traka u jednom redu prati pri skrolu; kad se prelomi u više redova, skroluje sa stranicom. */
    barSticky() {
      return this.barHeight <= STICKY_BAR_MAX;
    },
    rootStyle() {
      return {
        "--rd-top": `${this.topOffset}px`,
        "--rd-bar-h": `${this.barSticky ? this.barHeight : 0}px`,
      };
    },
    shortcuts() {
      return [
        { keys: "/", label: this.$t("search") },
        { keys: "r", label: this.$t("report_mobile.refresh") },
        { keys: "[  ]", label: this.$t("report_desktop.keys_day") },
        { keys: "1–9", label: this.$t("report_desktop.keys_report") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  mounted() {
    this.measureTop();
    this.observe();
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.measureTop);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.measureTop);
    window.removeEventListener("resize", this.onWindowResize);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.measureFrame);
    clearTimeout(this.toastTimer);
  },
  methods: {
    // ------------------------------------------------------------ izmere
    measureTop() {
      const bar = document.querySelector(".v-app-bar");
      this.topOffset = bar ? Math.round(bar.getBoundingClientRect().height) : DEFAULT_TOP;
    },
    observe() {
      const root = this.$refs.root;
      this.width = root.clientWidth;
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", this.onWindowResize);
        return;
      }
      // Stanje se menja tek u sledećem kadru: raspored koji se promeni usred obaveštenja posmatrača daje
      // grešku "ResizeObserver loop completed with undelivered notifications" (u razvoju crveni prozor).
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.measureFrame);
        this.measureFrame = requestAnimationFrame(this.measure);
      });
      this.resizeObserver.observe(root);
      if (this.$refs.bar) {
        this.resizeObserver.observe(this.$refs.bar.$el);
      }
    },
    measure() {
      const root = this.$refs.root;
      if (!root) {
        return;
      }
      this.width = root.clientWidth || this.width;
      const bar = this.$refs.bar?.$el;
      this.barHeight = bar ? Math.round(bar.getBoundingClientRect().height) : DEFAULT_BAR;
    },
    onWindowResize() {
      this.width = this.$refs.root?.clientWidth || this.width;
    },

    // ------------------------------------------------------------ radnje
    toggleDensity() {
      this.density = this.density === "compact" ? "comfortable" : "compact";
      try {
        localStorage.setItem(DENSITY_KEY, this.density);
      } catch (error) {
        // Bez localStorage-a gustina važi dok je stranica otvorena.
      }
    },
    showToast(text, { label = "", action = null, duration = 3200 } = {}) {
      clearTimeout(this.toastTimer);
      this.toast = { text, label, action };
      this.toastTimer = setTimeout(() => {
        this.toast = null;
      }, duration);
    },
    runToastAction() {
      const { action } = this.toast || {};
      this.toast = null;
      if (typeof action === "function") {
        action();
      }
    },

    // ------------------------------------------------------------ panel
    /**
     * entry: { key, title, subtitle, component, props, root, opener }
     * root=true zamenjuje ceo stek; opener je element na koji se vraća fokus pri zatvaranju.
     */
    openPanel(rawEntry) {
      if (!rawEntry) {
        return;
      }
      // zamrznuto: Vue ne prati komponentu, element ni ekran koji panel čita
      const entry = Object.freeze({ ...rawEntry });
      if (!this.panelStack.length) {
        this.panelOpener = entry.opener || document.activeElement;
      }
      if (entry.root) {
        this.panelStack = [entry];
        return;
      }
      const top = this.panelStack[this.panelStack.length - 1];
      if (top && top.key === entry.key) {
        return;
      }
      this.panelStack = [...this.panelStack, entry];
    },
    panelBack() {
      this.panelStack = this.panelStack.slice(0, -1);
      if (!this.panelStack.length) {
        this.restorePanelFocus();
      }
    },
    closePanel() {
      if (!this.panelStack.length) {
        return;
      }
      this.panelStack = [];
      this.restorePanelFocus();
    },
    restorePanelFocus() {
      const opener = this.panelOpener;
      this.panelOpener = null;
      if (opener && typeof opener.focus === "function" && document.contains(opener)) {
        this.$nextTick(() => opener.focus());
      }
    },

    // ------------------------------------------------------------ tastatura
    onKeydown(event) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const target = event.target;
      const typing =
        target &&
        (["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable);
      if (event.key === "Escape") {
        // Esc zatvara otvoreni Vuetify dijalog (npr. potvrdu), a ne panel iza njega. Dijalog se do ovog slušaoca
        // već zatvori i skine klasu, pa se gleda i da li taster dolazi iz njegovog sadržaja
        if ((target && target.closest && target.closest(".v-dialog__content")) || document.querySelector(".v-dialog--active")) {
          return;
        }
        if (this.helpOpen) {
          this.helpOpen = false;
        } else if (this.panelStack.length) {
          this.closePanel();
        }
        return;
      }
      if (typing || document.querySelector(".v-dialog--active")) {
        return;
      }
      if (event.key === "/") {
        event.preventDefault();
        this.$refs.bar?.focusSearch();
      } else if (event.key === "?") {
        this.helpOpen = !this.helpOpen;
      } else if (event.key === "r") {
        this.reload();
      } else if (event.key === "[" || event.key === "]") {
        this.shiftDay(event.key === "[" ? -1 : 1);
      } else if (/^[1-9]$/.test(event.key)) {
        const key = this.menuKeys[Number(event.key) - 1];
        if (key) {
          this.openScreen(key);
        }
      }
    },
  },
};
